import {
  Box,
  BoxProps,
  TextareaAutosize,
  TextareaAutosizeProps,
} from "@mui/material";
import { SInputProps } from "components/parts";
import { CSSProperties } from "react";
import {
  useController,
  UseControllerProps,
  useFormContext,
} from "react-hook-form";
import { useRecoilValue } from "recoil";
import { focusState } from "states";

type Props = Pick<UseControllerProps, "name"> &
  Partial<TextareaAutosizeProps> &
  Omit<SInputProps, "formRef" | "required" | "align"> &
  Pick<BoxProps, "gridColumn" | "gridRow" | "height">;

/**
 * テキストエリア
 *
 * ### Usage
 * ```
 * // フィールドはstring型とする。
 * type Fields = { text: string };
 *
 * // ダウンロードの方法
 * const handleClick = () => {
    const text = methods.getValues().text;
    if (typeof text === "undefined") return;
    // FileService.outputTextメソッドを利用する。
    outputText(text, "テキストエリア.txt");
 * };
 *
 * // name: フィールド名
 * // height: 高さを設定する(規定10rem)。幅は親要素のgridTemplateColumnで設定する。
 * // gridColumn / gridRow / フォーカス: 設定可能
 * return (
 *   <SFrame>
 *     <STextArea name="text" height="5rem" disabled />
 *     <SNormalButton label="ダウンロード" onClick={handleClick} />
 *   </SFrame>
 *);
 * ```
 * @param param
 * @returns
 */
export const STextArea = ({
  name,
  focusId,
  index,
  gridColumn,
  gridRow,
  height,
  ...props
}: Props): JSX.Element => {
  // フォーム設定
  const { control } = useFormContext();
  const { field } = useController({
    name,
    control,
  });
  // フォーカス設定
  const { focusRef, move } = useRecoilValue(focusState(focusId ?? ""));

  return (
    <Box gridColumn={gridColumn} gridRow={gridRow} height={height}>
      <TextareaAutosize
        {...props}
        {...field}
        ref={(element) => {
          field.ref(element);
          focusRef(index, element);
        }}
        onKeyDown={(event) => {
          if (event.ctrlKey && event.key === "Enter") {
            event.preventDefault();
            move(index, event);
            return;
          }
          props.onKeyDown?.call(this, event);
        }}
        style={style}
        tabIndex={index}
      />
    </Box>
  );
};

const style: CSSProperties = {
  overflow: "auto",
  resize: "none",
  height: "inherit",
  width: "-webkit-fill-available",
};
